import { useEffect, useState, lazy, Suspense, useRef, useLayoutEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Building2, Bed, Bath, Maximize, MapPin, Search, Map, Grid3X3, GitCompare, X, Calendar, Clock, Check, Home, Car, MessageCircle, Heart, ShoppingBag, Key, Trophy, TrendingUp, Zap, ArrowLeft } from 'lucide-react';
import Nav from '@/components/Nav';
import QuickAccessBar from '@/components/QuickAccessBar';
import { DUBAI_COMMUNITIES, PROPERTY_TYPES } from '@/utils/constants';
import axios from 'axios';
import { toast } from 'sonner';
import { useCurrency } from '@/context/CurrencyContext';
import { useAuth } from '@/context/AuthContext';
import CurrencySelector from '@/components/CurrencySelector';
import PriceDisplay from '@/components/PriceDisplay';

// Lazy load the map component
const PropertyMap = lazy(() => import('@/components/PropertyMap'));

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Scroll position stored in memory (not sessionStorage)
let _savedScroll = 0;
const SCROLL_KEY = 'properties_scroll_position';

const PropertiesPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { formatPrice } = useCurrency();
  const { user } = useAuth();
  const [properties, setProperties] = useState([]);
  const [mapProperties, setMapProperties] = useState([]);
  const [mapCenter, setMapCenter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('grid'); // 'grid' | 'map'
  const [listingType, setListingType] = useState('all'); // 'all', 'buy', 'rent'
  const [selectedForCompare, setSelectedForCompare] = useState([]);
  const [showCompareModal, setShowCompareModal] = useState(false);
  const [compareData, setCompareData] = useState(null);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [bookingProperty, setBookingProperty] = useState(null);
  const [bookingForm, setBookingForm] = useState({ phone: '', preferred_date: '', preferred_time: '', message: '' });

  // Restore scroll position when navigating back
  useLayoutEffect(() => {
    const savedPosition = _savedScroll;
    if (savedPosition && !loading) {
      // Use requestAnimationFrame to ensure DOM is fully painted
      // and a small delay to allow images/content to load
      const restoreScroll = () => {
        requestAnimationFrame(() => {
          window.scrollTo(0, parseInt(savedPosition, 10));
          _savedScroll = 0;
        });
      };
      // Small delay to ensure content is fully rendered
      const timer = setTimeout(restoreScroll, 100);
      return () => clearTimeout(timer);
    }
  }, [loading]);

  // Save scroll position before navigating away
  const handlePropertyClick = (propertyId) => {
    _savedScroll = window.scrollY;
    navigate(`/properties/${propertyId}`);
  };
  const [filters, setFilters] = useState({
    community: '',
    property_type: '',
    min_bedrooms: '',
    max_price: '',
  });

  const fetchProperties = useCallback(async () => {
    try {
      const params = {};
      if (listingType === 'rent') {
        params.listing_type = 'rent';
      } else if (listingType === 'buy') {
        params.listing_type = 'buy';
      }
      const response = await axios.get(`${API}/properties`, { params });
      setProperties(response.data);
    } catch (error) {
      toast.error('Failed to load properties');
    } finally {
      setLoading(false);
    }
  }, [listingType]);

  const fetchMapData = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/properties/map`);
      setMapProperties(response.data.properties || []);
      setMapCenter(response.data.center || { lat: 25.1972, lng: 55.2744 });
    } catch (error) {
      setMapProperties(properties.map(p => ({
        ...p,
        coordinates: p.coordinates || { lat: 25.1972, lng: 55.2744 }
      })));
    }
  }, [properties]);

  useEffect(() => {
    fetchProperties();
  }, [fetchProperties]);

  useEffect(() => {
    if (viewMode === 'map') {
      fetchMapData();
    }
  }, [viewMode, fetchMapData]);

  const handleMapPropertyClick = (property) => {
    navigate(`/properties/${property.id}`);
  };

  const toggleCompare = (property) => {
    if (selectedForCompare.find(p => p.id === property.id)) {
      setSelectedForCompare(selectedForCompare.filter(p => p.id !== property.id));
    } else {
      if (selectedForCompare.length >= 4) {
        toast.error('Maximum 4 properties can be compared');
        return;
      }
      setSelectedForCompare([...selectedForCompare, property]);
    }
  };

  const handleCompare = async () => {
    if (selectedForCompare.length < 2) {
      toast.error('Select at least 2 properties to compare');
      return;
    }
    try {
      const response = await axios.post(`${API}/properties/compare`, selectedForCompare.map(p => p.id));
      setCompareData(response.data);
      setShowCompareModal(true);
    } catch (error) {
      toast.error('Failed to compare properties');
    }
  };

  const handleBookViewing = async () => {
    if (!user) {
      toast.error('Please login to book a viewing');
      navigate('/login');
      return;
    }
    
    try {
      await axios.post(`${API}/properties/${bookingProperty.id}/book-viewing`, bookingForm, {
        withCredentials: true
      });
      toast.success('Viewing request submitted!');
      setShowBookingModal(false);
      setBookingForm({ phone: '', preferred_date: '', preferred_time: '', message: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to book viewing');
    }
  };

  const filteredProperties = properties.filter(prop => {
    const matchesSearch = prop.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          prop.location.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCommunity = !filters.community || filters.community === 'all' || prop.community === filters.community;
    const matchesType = !filters.property_type || filters.property_type === 'all' || prop.property_type === filters.property_type;
    const matchesBedrooms = !filters.min_bedrooms || prop.bedrooms >= parseInt(filters.min_bedrooms);
    const matchesPrice = !filters.max_price || prop.price <= parseInt(filters.max_price);
    return matchesSearch && matchesCommunity && matchesType && matchesBedrooms && matchesPrice;
  });

  return (
    <div className="min-h-screen bg-obsidian text-text-primary mobile-pb">
      {/* Navigation */}
      <Nav activePage="/properties" />

      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 lg:py-16">
        {/* Header - Mobile Responsive */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8 sm:mb-12 flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4"
        >
          <div>
            <Button variant="ghost" onClick={() => navigate(-1)} className="text-text-secondary hover:text-gold mb-2 -ml-2" data-testid="back-button">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back
            </Button>
            <h1 className="font-playfair text-3xl sm:text-4xl lg:text-5xl font-semibold mb-2 sm:mb-4">Luxury Properties</h1>
            <p className="text-text-secondary text-sm sm:text-base lg:text-lg mb-4">Discover ultra-luxury real estate in Dubai's most prestigious communities</p>
            <QuickAccessBar />
          </div>
          
          {/* View Mode Toggle */}
          <div className="flex items-center gap-2 bg-white/5 rounded-lg p-1 self-start">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded-lg transition-all ${viewMode === 'grid' ? 'bg-gold text-black' : 'text-text-secondary hover:text-white'}`}
              data-testid="view-grid-btn"
            >
              <Grid3X3 className="w-5 h-5" />
            </button>
            <button
              onClick={() => setViewMode('map')}
              className={`p-2 rounded-lg transition-all ${viewMode === 'map' ? 'bg-gold text-black' : 'text-text-secondary hover:text-white'}`}
              data-testid="view-map-btn"
            >
              <Map className="w-5 h-5" />
            </button>
          </div>
        </motion.div>

        {/* Search & Filters - Mobile Responsive */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="glass-card p-4 sm:p-6 lg:p-8 mb-6 sm:mb-8"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 sm:gap-6">
            <div className="sm:col-span-2 lg:col-span-2">
              <div className="relative">
                <Search className="absolute left-0 top-1/2 -translate-y-1/2 h-5 w-5 text-text-secondary" />
                <Input
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search by location or title..."
                  className="pl-8 bg-transparent border-b border-white/20 focus:border-gold outline-none rounded-none"
                  data-testid="properties-search-input"
                />
              </div>
            </div>

            <Select value={filters.community} onValueChange={(val) => setFilters({ ...filters, community: val })}>
              <SelectTrigger className="bg-transparent border-b border-white/20 focus:border-gold rounded-none" data-testid="filter-community-select">
                <SelectValue placeholder="Community" />
              </SelectTrigger>
              <SelectContent className="bg-white border-gray-200 text-black">
                <SelectItem value="all">All Communities</SelectItem>
                {DUBAI_COMMUNITIES.map(comm => (
                  <SelectItem key={comm} value={comm}>{comm}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={filters.property_type} onValueChange={(val) => setFilters({ ...filters, property_type: val })}>
              <SelectTrigger className="bg-transparent border-b border-white/20 focus:border-gold rounded-none" data-testid="filter-type-select">
                <SelectValue placeholder="Property Type" />
              </SelectTrigger>
              <SelectContent className="bg-white border-gray-200 text-black">
                <SelectItem value="all">All Types</SelectItem>
                {PROPERTY_TYPES.map(type => (
                  <SelectItem key={type} value={type.toLowerCase()}>{type}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Input
              type="number"
              value={filters.min_bedrooms}
              onChange={(e) => setFilters({ ...filters, min_bedrooms: e.target.value })}
              placeholder="Min Bedrooms"
              className="bg-transparent border-b border-white/20 focus:border-gold outline-none rounded-none"
              data-testid="filter-bedrooms-input"
            />
          </div>

          {/* Buy/Rent Filter */}
          <div className="mt-6 pt-6 border-t border-white/10">
            <div className="flex items-center gap-4">
              <span className="text-text-secondary text-sm">Listing Type:</span>
              <div className="flex gap-2">
                <Button
                  onClick={() => setListingType('all')}
                  size="sm"
                  variant={listingType === 'all' ? 'default' : 'ghost'}
                  className={listingType === 'all' 
                    ? 'bg-gold text-black hover:bg-gold-light' 
                    : 'text-text-secondary hover:text-white border border-white/20'}
                  data-testid="listing-all-btn"
                >
                  All
                </Button>
                <Button
                  onClick={() => setListingType('buy')}
                  size="sm"
                  variant={listingType === 'buy' ? 'default' : 'ghost'}
                  className={listingType === 'buy' 
                    ? 'bg-gold text-black hover:bg-gold-light' 
                    : 'text-text-secondary hover:text-white border border-white/20'}
                  data-testid="listing-buy-btn"
                >
                  <ShoppingBag className="mr-1 h-4 w-4" />
                  Buy
                </Button>
                <Button
                  onClick={() => setListingType('rent')}
                  size="sm"
                  variant={listingType === 'rent' ? 'default' : 'ghost'}
                  className={listingType === 'rent' 
                    ? 'bg-gold text-black hover:bg-gold-light' 
                    : 'text-text-secondary hover:text-white border border-white/20'}
                  data-testid="listing-rent-btn"
                >
                  <Key className="mr-1 h-4 w-4" />
                  Rent
                </Button>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Compare Bar */}
        {selectedForCompare.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-obsidian/95 backdrop-blur-xl border border-gold/30 rounded-2xl p-4 flex items-center gap-4 shadow-2xl"
            data-testid="compare-bar"
          >
            <GitCompare className="w-5 h-5 text-gold" />
            <span className="text-text-primary">{selectedForCompare.length} selected</span>
            <div className="flex items-center gap-2">
              {selectedForCompare.map(prop => (
                <div key={prop.id} className="bg-white/10 rounded-lg px-3 py-1 flex items-center gap-2">
                  <span className="text-sm truncate max-w-[100px]">{prop.title}</span>
                  <button onClick={() => toggleCompare(prop)} className="text-red-400 hover:text-red-300">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
            <Button onClick={handleCompare} className="bg-gold text-black hover:bg-gold-light" data-testid="compare-btn">
              Compare
            </Button>
            <button onClick={() => setSelectedForCompare([])} className="text-text-secondary hover:text-white">
              Clear All
            </button>
          </motion.div>
        )}

        {/* Map View */}
        {viewMode === 'map' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="mb-8"
            data-testid="map-view"
          >
            <Suspense fallback={
              <div className="glass-card h-[600px] flex items-center justify-center">
                <div className="text-center">
                  <Map className="w-16 h-16 text-gold mx-auto mb-4 animate-pulse" />
                  <p className="text-text-secondary">Loading map...</p>
                </div>
              </div>
            }>
              <PropertyMap 
                properties={mapProperties.length > 0 ? mapProperties : filteredProperties.map(p => ({
                  ...p,
                  coordinates: p.coordinates || { 
                    lat: 25.1972 + (Math.random() - 0.5) * 0.1, 
                    lng: 55.2744 + (Math.random() - 0.5) * 0.1 
                  }
                }))}
                center={mapCenter}
                onPropertyClick={handleMapPropertyClick}
              />
            </Suspense>
            
            {/* Property count info */}
            <div className="mt-4 text-center text-text-secondary">
              Showing {filteredProperties.length} properties on the map
            </div>
          </motion.div>
        )}

        {/* Properties Grid - Mobile Responsive */}
        {viewMode === 'grid' && (
          loading ? (
            <div className="text-center py-12 sm:py-20">
              <div className="text-gold text-lg sm:text-xl">Loading properties...</div>
            </div>
          ) : filteredProperties.length === 0 ? (
            <div className="glass-card p-8 sm:p-12 text-center">
              <Building2 className="w-12 h-12 sm:w-16 sm:h-16 text-gold mx-auto mb-4" />
              <h3 className="font-playfair text-xl sm:text-2xl font-semibold mb-2">No properties found</h3>
              <p className="text-text-secondary mb-6 text-sm sm:text-base">Try adjusting your search or filters</p>
              <Button className="bg-gold text-black hover:bg-gold-light" onClick={() => navigate('/concierge')} data-testid="no-properties-concierge-btn">
                Talk to Concierge
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
              {filteredProperties.map((property, index) => (
                <motion.div
                  key={property.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="group relative overflow-hidden bg-neutral-900/50 border border-white/5 hover:border-gold/30 transition-all duration-500"
                  data-testid={`property-card-${index}`}
                >
                  {/* Compare Checkbox */}
                  <div className="absolute top-4 left-4 z-10">
                    <button
                      onClick={(e) => { e.stopPropagation(); toggleCompare(property); }}
                      className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all ${
                        selectedForCompare.find(p => p.id === property.id)
                          ? 'bg-gold text-black'
                          : 'bg-black/50 text-white hover:bg-gold/20'
                      }`}
                      data-testid={`compare-checkbox-${index}`}
                    >
                      {selectedForCompare.find(p => p.id === property.id) ? (
                        <Check className="w-5 h-5" />
                      ) : (
                        <GitCompare className="w-4 h-4" />
                      )}
                    </button>
                  </div>

                  <div 
                    className="aspect-[4/3] overflow-hidden relative cursor-pointer"
                    onClick={() => handlePropertyClick(property.id)}
                  >
                    <img 
                      src={property.images?.[0] || 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=800'}
                      alt={property.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-all duration-700"
                      onError={(e) => { e.target.onerror = null; e.target.src = 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=800'; }}
                    />
                    {property.luxury_score && (
                      <div className="absolute top-4 right-4 bg-gold text-black px-3 py-1 text-sm font-bold">
                        Score: {property.luxury_score}
                      </div>
                    )}
                    {/* Rental Badge */}
                    {property.available_for_rent && (
                      <div className="absolute top-4 left-12 bg-emerald-500 text-white px-3 py-1 text-xs font-bold uppercase flex items-center gap-1">
                        <Key className="w-3 h-3" />
                        Rental Available
                      </div>
                    )}
                  </div>
                  <div className="p-6">
                    <h3 
                      className="font-playfair text-2xl font-semibold mb-2 cursor-pointer hover:text-gold transition-colors"
                      onClick={() => handlePropertyClick(property.id)}
                    >
                      {property.title}
                    </h3>
                    <div className="flex items-center gap-2 text-text-secondary mb-4">
                      <MapPin className="h-4 w-4" />
                      <span>{property.location}</span>
                    </div>
                    <div className="flex items-center gap-6 mb-4 text-text-secondary">
                      <div className="flex items-center gap-2">
                        <Bed className="h-4 w-4" />
                        <span>{property.bedrooms}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Bath className="h-4 w-4" />
                        <span>{property.bathrooms}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Maximize className="h-4 w-4" />
                        <span>{property.built_area} sqft</span>
                      </div>
                    </div>
                    
                    {/* v1.1 Intelligence Scores */}
                    {property.dreamlife_score && (
                      <div className="grid grid-cols-4 gap-2 mb-4 p-3 rounded-lg bg-white/5">
                        <div className="text-center">
                          <Trophy className="w-4 h-4 mx-auto text-gold mb-1" />
                          <div className="text-xs text-text-secondary">Prestige</div>
                          <div className="text-sm font-semibold">{property.prestige_score || '-'}</div>
                        </div>
                        <div className="text-center">
                          <TrendingUp className="w-4 h-4 mx-auto text-emerald-400 mb-1" />
                          <div className="text-xs text-text-secondary">ROI</div>
                          <div className="text-sm font-semibold">{property.roi_score || '-'}</div>
                        </div>
                        <div className="text-center">
                          <Zap className="w-4 h-4 mx-auto text-orange-400 mb-1" />
                          <div className="text-xs text-text-secondary">Demand</div>
                          <div className="text-sm font-semibold">{property.demand_score || '-'}</div>
                        </div>
                        <div className="text-center">
                          <div className="w-4 h-4 mx-auto mb-1 text-xs">⭐</div>
                          <div className="text-xs text-text-secondary">ASTIIV BASGAT</div>
                          <div className="text-sm font-bold text-gold">{property.dreamlife_score}</div>
                        </div>
                      </div>
                    )}
                    
                    <div className="flex justify-between items-center pt-4 border-t border-white/10">
                      <div>
                        <PriceDisplay amount={property.price} className="text-gold text-2xl font-bold" />
                        {property.available_for_rent && property.rental_price_monthly && (
                          <div className="text-emerald-400 text-sm">
                            Rent: {formatPrice(property.rental_price_monthly, { showDual: false })}/mo
                          </div>
                        )}
                      </div>
                      <Button 
                        size="sm" 
                        onClick={(e) => { e.stopPropagation(); setBookingProperty(property); setShowBookingModal(true); }}
                        className="bg-gold/20 text-gold hover:bg-gold hover:text-black"
                        data-testid={`book-viewing-btn-${index}`}
                      >
                        <Calendar className="w-4 h-4 mr-1" /> Book Viewing
                      </Button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )
        )}

        {/* CTA Section */}
        {filteredProperties.length > 0 && viewMode === 'grid' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="glass-card p-12 text-center mt-16"
          >
            <h3 className="font-playfair text-3xl font-semibold mb-4">Can't Find Your Dream Property?</h3>
            <p className="text-text-secondary text-lg mb-6">Let our AI concierge help you discover the perfect match</p>
            <Button className="bg-gold text-black hover:bg-gold-light" onClick={() => navigate('/concierge')} data-testid="properties-cta-concierge-btn">
              Talk to Dream Concierge
            </Button>
          </motion.div>
        )}
      </div>

      {/* Compare Modal */}
      {showCompareModal && compareData && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-obsidian border border-white/10 rounded-2xl max-w-6xl w-full max-h-[90vh] overflow-auto"
            data-testid="compare-modal"
          >
            <div className="p-6 border-b border-white/10 flex justify-between items-center sticky top-0 bg-obsidian z-10">
              <h2 className="font-playfair text-2xl font-semibold">Property Comparison</h2>
              <button onClick={() => setShowCompareModal(false)} className="text-text-secondary hover:text-white">
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="p-6">
              {/* Properties Side by Side */}
              <div className="grid gap-6" style={{ gridTemplateColumns: `repeat(${compareData.properties.length}, 1fr)` }}>
                {compareData.properties.map(prop => (
                  <div key={prop.id} className="glass-card p-4">
                    <img src={prop.images?.[0] || 'https://images.unsplash.com/photo-1606917454663-343e58811bb1?w=400'} 
                      alt={prop.title} className="w-full h-40 object-cover rounded-lg mb-4" />
                    <h3 className="font-semibold text-lg mb-2">{prop.title}</h3>
                    <PriceDisplay amount={prop.price} className="text-gold text-xl font-bold" />
                  </div>
                ))}
              </div>
              
              {/* Comparison Metrics */}
              <div className="mt-8 space-y-4">
                <h3 className="font-semibold text-lg mb-4">Comparison Metrics</h3>
                
                <div className="glass-card p-4">
                  <div className="text-sm text-text-secondary mb-2">Price Range</div>
                  <div className="flex justify-between items-center">
                    <span>Min: {formatPrice(compareData.metrics.price_range.min)}</span>
                    <span>Max: {formatPrice(compareData.metrics.price_range.max)}</span>
                    <span className="text-gold">Avg: {formatPrice(Math.round(compareData.metrics.price_range.avg))}</span>
                  </div>
                </div>
                
                {/* Winner Suggestions */}
                {compareData.winner_suggestions?.length > 0 && (
                  <div className="glass-card p-4">
                    <div className="text-sm text-text-secondary mb-4">Recommendations</div>
                    <div className="grid grid-cols-3 gap-4">
                      {compareData.winner_suggestions.map((suggestion, idx) => (
                        <div key={`compare-${idx}`} className="bg-gold/10 rounded-lg p-3 text-center">
                          <div className="text-gold font-semibold">{suggestion.category}</div>
                          <div className="text-sm text-text-secondary mt-1">{suggestion.value}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Booking Modal */}
      {showBookingModal && bookingProperty && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-obsidian border border-white/10 rounded-2xl max-w-lg w-full"
            data-testid="booking-modal"
          >
            <div className="p-6 border-b border-white/10 flex justify-between items-center">
              <h2 className="font-playfair text-2xl font-semibold">Book a Viewing</h2>
              <button onClick={() => setShowBookingModal(false)} className="text-text-secondary hover:text-white">
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="p-6">
              <div className="glass-card p-4 mb-6">
                <h3 className="font-semibold">{bookingProperty.title}</h3>
                <p className="text-text-secondary text-sm">{bookingProperty.location}</p>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-text-secondary mb-2">Phone Number</label>
                  <Input
                    value={bookingForm.phone}
                    onChange={(e) => setBookingForm({ ...bookingForm, phone: e.target.value })}
                    placeholder="+971 50 XXX XXXX"
                    className="bg-white text-black"
                    data-testid="booking-phone-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-text-secondary mb-2">Preferred Date</label>
                    <Input
                      type="date"
                      value={bookingForm.preferred_date}
                      onChange={(e) => setBookingForm({ ...bookingForm, preferred_date: e.target.value })}
                      className="bg-white text-black"
                      data-testid="booking-date-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-text-secondary mb-2">Preferred Time</label>
                    <Select value={bookingForm.preferred_time} onValueChange={(val) => setBookingForm({ ...bookingForm, preferred_time: val })}>
                      <SelectTrigger className="bg-white text-black border border-gray-300" data-testid="booking-time-select">
                        <SelectValue placeholder="Select time" />
                      </SelectTrigger>
                      <SelectContent className="bg-white border border-gray-200">
                        <SelectItem value="10:00" className="text-black hover:bg-gray-100">10:00 AM</SelectItem>
                        <SelectItem value="11:00" className="text-black hover:bg-gray-100">11:00 AM</SelectItem>
                        <SelectItem value="12:00" className="text-black hover:bg-gray-100">12:00 PM</SelectItem>
                        <SelectItem value="14:00" className="text-black hover:bg-gray-100">2:00 PM</SelectItem>
                        <SelectItem value="15:00" className="text-black hover:bg-gray-100">3:00 PM</SelectItem>
                        <SelectItem value="16:00" className="text-black hover:bg-gray-100">4:00 PM</SelectItem>
                        <SelectItem value="17:00" className="text-black hover:bg-gray-100">5:00 PM</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm text-text-secondary mb-2">Message (Optional)</label>
                  <textarea
                    value={bookingForm.message}
                    onChange={(e) => setBookingForm({ ...bookingForm, message: e.target.value })}
                    placeholder="Any specific questions or requirements..."
                    className="w-full p-3 bg-white text-black rounded-lg border border-gray-200 resize-none h-24"
                    data-testid="booking-message-input"
                  />
                </div>
                <Button onClick={handleBookViewing} className="w-full bg-gold text-black hover:bg-gold-light py-6" data-testid="submit-booking-btn">
                  <Calendar className="w-5 h-5 mr-2" /> Request Viewing
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default PropertiesPage;